/**
 * CEO Command Center - Document Store
 * 
 * Zustand store for managing document upload history.
 * Tracks documents uploaded to domains and initiatives.
 * 
 * @module stores/documentStore
 * @version 10.2.0
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

// ============================================
// TYPES
// ============================================

export interface DocumentRecord {
  id: string;
  filename: string;
  fileType: string;
  sourceId: string; // domain name or initiative ID
  sourceType: 'domain' | 'initiative';
  uploadedAt: string;
  status: 'pending' | 'processing' | 'processed' | 'failed';
  extractedTasks: number;
  fileSize?: number;
  error?: string;
  contentPreview?: string; // First 500 chars for re-processing
}

interface DocumentState {
  // Data
  documents: DocumentRecord[];
  
  // Actions
  addDocument: (doc: Omit<DocumentRecord, 'id' | 'uploadedAt'>) => DocumentRecord;
  updateDocument: (id: string, updates: Partial<DocumentRecord>) => void;
  deleteDocument: (id: string) => void;
  
  // Queries
  getDocumentsBySource: (sourceId: string, sourceType: 'domain' | 'initiative') => DocumentRecord[];
  getRecentDocuments: (limit?: number) => DocumentRecord[];
  
  // Clear
  clearHistory: (sourceId?: string) => void;
}

// ============================================
// UTILITIES
// ============================================

const generateId = () => `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// ============================================
// STORE
// ============================================

export const useDocumentStore = create<DocumentState>()(
  devtools(
    persist(
      (set, get) => ({
        documents: [],
        
        // Add document
        addDocument: (docData) => {
          const doc: DocumentRecord = {
            id: generateId(),
            ...docData,
            uploadedAt: new Date().toISOString(),
          };
          
          set(state => ({
            documents: [doc, ...state.documents].slice(0, 100), // Keep last 100
          }));
          
          return doc;
        },
        
        // Update document
        updateDocument: (id, updates) => {
          set(state => ({
            documents: (state.documents || []).map(doc =>
              doc.id === id ? { ...doc, ...updates } : doc
            ),
          }));
        },
        
        // Delete document
        deleteDocument: (id) => {
          set(state => ({
            documents: (state.documents || []).filter(doc => doc.id !== id),
          }));
        },
        
        // Get documents by source
        getDocumentsBySource: (sourceId, sourceType) => {
          return (get().documents || []).filter(
            doc => doc.sourceId === sourceId && doc.sourceType === sourceType
          );
        },
        
        // Get recent documents
        getRecentDocuments: (limit = 10) => {
          return (get().documents || []).slice(0, limit);
        },
        
        // Clear history
        clearHistory: (sourceId) => {
          if (sourceId) {
            set(state => ({
              documents: (state.documents || []).filter(doc => doc.sourceId !== sourceId),
            }));
          } else {
            set({ documents: [] });
          }
        },
      }),
      {
        name: 'sentiance_document_history',
        version: 1,
        merge: (persistedState: any, currentState: any) => ({
          ...currentState,
          ...persistedState,
          documents: Array.isArray(persistedState?.documents) ? persistedState.documents : [],
        }),
      }
    ),
    { name: 'ceo-document-store' }
  )
);

// ============================================
// SELECTORS
// ============================================

export const useDomainDocuments = (domain: string) => {
  return useDocumentStore(state =>
    (state.documents || []).filter(doc => doc.sourceId === domain && doc.sourceType === 'domain')
  );
};

export const useInitiativeDocuments = (initiativeId: string) => {
  return useDocumentStore(state =>
    (state.documents || []).filter(doc => doc.sourceId === initiativeId && doc.sourceType === 'initiative')
  );
};
